import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { detailedInvoiceService } from '../../services/dataService';
import usePageTitle from '../../utils/usePageTitle';
import './DetailedInvoiceDetails.css';

const WORK_TYPE_LABELS = {
  AUD: 'Audit', CET: 'CET', LAB: 'Labeling', REW: 'Rework',
  SCR: 'Screening', SQV: 'SQV', UPG: 'Upgrade', CFG: 'Configuration',
  RBX: 'Rebox', BXR: 'Rbox', C55: 'CD55', PZG: 'Palletizing',
  DFG: 'Deconfiguration', STG: 'Sorting', PCI: 'Power Cord Insert', BDG: 'Bundling'
};

const DetailedInvoiceDetails = () => {
  const { projectCode } = useParams();
  usePageTitle(`Detailed Invoice - ${projectCode}`);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [projectList, setProjectList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectCode);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [effortChangeFlag, setEffortChangeFlag] = useState('0');
  const [hasChanges, setHasChanges] = useState(false);

  const loadInvoiceData = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await detailedInvoiceService.getDetailedInvoice(code);
      setInvoice(response.invoice);
      setEffortChangeFlag(response.invoice.effortChangeFlag || '0');
      setFormData({
        manager: response.invoice.manager || '',
        cc: response.invoice.cc || '',
        creditAcct: response.invoice.creditAcct || '',
        approvedBy: response.invoice.approvedBy || '',
        approvedDate: response.invoice.approvedDate || ''
      });

      if (!projectList.length) {
        const projectsResponse = await detailedInvoiceService.getProjectCodes(code.substring(0, code.length - 3));
        setProjectList(projectsResponse.projectCodes || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load invoice data');
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  }, [projectList.length]);

  useEffect(() => {
    if (selectedProject) {
      loadInvoiceData(selectedProject);
    }
  }, [selectedProject, loadInvoiceData]);

  const handleProjectChange = (e) => {
    const newProject = e.target.value;
    setSelectedProject(newProject);
    navigate(`/invoice/detailed/${newProject}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    const confirmSubmit = window.confirm(
      invoice.projectStatus === 'INV' 
        ? 'Invoice has been already submitted. Do you want to send the details again to manager by email?'
        : 'Are you sure you want to submit this invoice?'
    );

    if (!confirmSubmit) return;

    try {
      setSubmitting(true);
      setError(null);
      
      const submitData = {
        ...invoice,
        ...formData,
        status: 'ACT',
        effortChangeFlag
      };

      await detailedInvoiceService.submitInvoice(submitData);
      setSuccess('Invoice submitted successfully');
      setHasChanges(false);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => {
        setSuccess(null);
        navigate('/invoice/detailed');
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to submit invoice');
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!formData.approvedBy || !formData.approvedDate) {
      setError('Approved by and approved date are required');
      return;
    }

    const confirmApprove = window.confirm('Are you sure you want to approve this invoice?');
    if (!confirmApprove) return;

    try {
      setSubmitting(true);
      setError(null);

      const approveData = {
        ...invoice,
        ...formData,
        status: 'APP',
        effortChangeFlag
      };

      await detailedInvoiceService.approveInvoice(approveData);
      setSuccess('Invoice approved successfully');
      setHasChanges(false);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => {
        setSuccess(null);
        navigate('/invoice/detailed');
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to approve invoice');
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    if (hasChanges) {
      const confirmLeave = window.confirm('Do you want to save the changes?');
      if (confirmLeave) {
        handleSubmit();
        return;
      }
    }
    navigate('/invoice/detailed');
  };

  if (loading) {
    return (
      <div className="invoice-details-container" role="main" aria-busy="true">
        <div className="loading-spinner" aria-live="polite">
          <span className="sr-only">Loading invoice details...</span>
          Loading...
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-details-container" role="main">
        <div className="alert alert-error" role="alert">Invoice not found</div>
        <button onClick={() => navigate('/invoice/detailed')} className="btn-secondary">Back to List</button>
      </div>
    );
  }

  const isEditable = ['ACT', 'CLO', 'INV'].includes(invoice.projectStatus);
  const isCompleted = invoice.woEffortStatus === 'Completed';

  return (
    <div className="invoice-details-container" role="main">
      <div className="action-bar no-print">
        {isEditable && (
          <button onClick={handleSubmit} className="btn-primary" disabled={!isCompleted || submitting} aria-label="Submit invoice">
            Submit
          </button>
        )}
        <button onClick={handlePrint} className="btn-secondary" aria-label="Print invoice">Print</button>
        <button onClick={handleBack} className="btn-secondary" aria-label="Go back">Back</button>
        {isEditable && invoice.projectStatus === 'INV' && (
          <button onClick={handleApprove} className="btn-success" disabled={!isCompleted || submitting} aria-label="Approve invoice">
            Approve
          </button>
        )}
      </div>

      {error && (
        <Alert severity="error" role="alert" aria-live="assertive" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" role="status" aria-live="polite" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {effortChangeFlag === '1' && (
        <Alert severity="info" role="status" aria-live="polite" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }}>
          Effort has been changed. Please review the invoice details.
        </Alert>
      )}
      {!isCompleted && (
        <Alert severity="warning" role="status" aria-live="polite" sx={{ mb: 2, position: 'sticky', top: 0, zIndex: 1000 }}>
          Work Order and Effort must be completed before submitting invoice.
        </Alert>
      )}

      <header className="invoice-header">
        <h1 className="invoice-title">DETAILED INVOICE</h1>
        <h2 className="invoice-subtitle">Sony Electronics Inc. - Quality Assurance Dept.</h2>
      </header>

      <div className="project-code-section">
        <strong>Project Code: </strong>
        {projectList.length > 0 ? (
          <select 
            className="project-select" 
            value={selectedProject} 
            onChange={handleProjectChange}
            aria-label="Select project code"
          >
            {projectList.map(code => <option key={code} value={code}>{code}</option>)}
          </select>
        ) : (
          <span>{invoice.code}</span>
        )}
      </div>

      <div className="invoice-content">
        <div className="invoice-grid">
          <section className="billing-section" aria-labelledby="billing-heading">
            <h3 id="billing-heading" className="sr-only">Billing Information</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <th scope="row">Billed To:</th>
                  <td>{invoice.name}</td>
                </tr>
                {invoice.address1 && <tr><td></td><td>{invoice.address1}</td></tr>}
                {invoice.address2 && <tr><td></td><td>{invoice.address2}</td></tr>}
                {invoice.address3 && <tr><td></td><td>{invoice.address3}</td></tr>}
                <tr>
                  <th scope="row">Attn:</th>
                  <td>{invoice.reqName} - {invoice.picName}</td>
                </tr>
                <tr>
                  <th scope="row"><label htmlFor="manager-input">Manager:</label></th>
                  <td>
                    {isEditable ? (
                      <input type="text" id="manager-input" name="manager" value={formData.manager} onChange={handleInputChange} maxLength="500" className="form-input" aria-label="Manager email addresses" />
                    ) : invoice.manager}
                  </td>
                </tr>
                <tr>
                  <th scope="row"><label htmlFor="cc-input">CC:</label></th>
                  <td>
                    {isEditable ? (
                      <input type="text" id="cc-input" name="cc" value={formData.cc} onChange={handleInputChange} maxLength="500" className="form-input" aria-label="CC email addresses" />
                    ) : invoice.cc}
                  </td>
                </tr>
                <tr>
                  <td colSpan="2"><em>Note: Separate multiple email addresses with semicolon (;)</em></td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="invoice-info-section" aria-labelledby="invoice-info-heading">
            <h3 id="invoice-info-heading" className="sr-only">Invoice Information</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <th scope="row">Billing Date:</th>
                  <td>{invoice.billingDate}</td>
                </tr>
                <tr>
                  <th scope="row">Business Group:</th>
                  <td>{invoice.businessGroup}</td>
                </tr>
                <tr>
                  <th scope="row">Debit Account:</th>
                  <td>{invoice.debitAcct}</td>
                </tr>
                <tr>
                  <th scope="row"><label htmlFor="credit-acct-input">Credit Account:</label></th>
                  <td>
                    {isEditable ? (
                      <input type="text" id="credit-acct-input" name="creditAcct" value={formData.creditAcct} onChange={handleInputChange} maxLength="50" className="form-input" aria-label="Credit account" />
                    ) : invoice.creditAcct}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <section className="work-info-section" aria-labelledby="work-info-heading">
          <h3 id="work-info-heading" className="sr-only">Work Information</h3>
          <p><strong>Type of Work:</strong> {WORK_TYPE_LABELS[invoice.workType] || invoice.workType}</p>
          <p><strong>Work Performed:</strong> {invoice.workPerformed}</p>
        </section>

        <section className="details-section" aria-labelledby="details-heading">
          <h3 id="details-heading" className="sr-only">Invoice Details</h3>
          <table className="details-table" aria-label="Invoice line items and totals">
            <tbody>
              {invoice.detailsList?.map((detail, index) => (
                <React.Fragment key={detail.id}>
                  <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td colSpan="5"><strong>Model Code:</strong> {detail.code}</td>
                    <td className="text-right"><strong>Qty:</strong> {detail.quantity}</td>
                  </tr>

                  {detail.invoiceType === 'V' && (
                    <>
                    </>
                  )}

                  {detail.invoiceType === 'F' && (
                    <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td>Unit Rate</td>
                      <td className="text-right">{detail.quantity}</td>
                      <td>@ ${detail.unitRate}</td>
                      <td>Per Unit</td>
                      <td className="text-right">${detail.unitCostTotal}</td>
                    </tr>
                  )}

                  <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td colSpan="4"><strong>Materials & Supplies:</strong></td>
                    <td className="text-right"><strong>Amount</strong></td>
                  </tr>
                  <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td colSpan="4" className="text-right">Materials Total</td>
                    <td className="text-right">${detail.materialsTotal}</td>
                  </tr>
                  <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td colSpan="4" className="text-right"><strong>Invoice Total</strong></td>
                    <td className="text-right"><strong>${detail.invoiceTotal}</strong></td>
                  </tr>
                  <tr className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td colSpan="4" className="text-right">Unit Cost</td>
                    <td className="text-right">${detail.unitCost}</td>
                  </tr>
                </React.Fragment>
              ))}

              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Cumulative Quantity</strong></td>
                <td className="text-right"><strong>{invoice.cumulativeQty}</strong></td>
              </tr>
              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Cumulative Unit Rate</strong></td>
                <td className="text-right"><strong>${invoice.cumulativeTotal}</strong></td>
              </tr>
              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Cumulative Materials</strong></td>
                <td className="text-right"><strong>${invoice.cumulativeMaterials}</strong></td>
              </tr>
              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Cumulative Total Invoice</strong></td>
                <td className="text-right"><strong>${invoice.cumulativeTotalInvoice}</strong></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="approval-section" aria-labelledby="approval-heading">
          <h3 id="approval-heading" className="sr-only">Approval Information</h3>
          <div className="approval-info">
            <p>
              <label htmlFor="approved-by-input"><strong>Approved By:</strong></label>{' '}
              {isEditable ? (
                <input type="text" id="approved-by-input" name="approvedBy" value={formData.approvedBy} onChange={handleInputChange} maxLength="50" className="form-input" aria-label="Approved by" />
              ) : invoice.approvedBy}
            </p>
            <p>
              <label htmlFor="approved-date-input"><strong>Approved Date:</strong></label>{' '}
              {isEditable ? (
                <input type="date" id="approved-date-input" name="approvedDate" value={formData.approvedDate} onChange={handleInputChange} className="form-input" aria-label="Approved date" />
              ) : invoice.approvedDate}
            </p>
          </div>
        </section>
      </div>

      <div className="action-bar no-print">
        {isEditable && (
          <button onClick={handleSubmit} className="btn-primary" disabled={!isCompleted || submitting} aria-label="Submit invoice">
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        )}
        <button onClick={handlePrint} className="btn-secondary" aria-label="Print invoice">Print</button>
        <button onClick={handleBack} className="btn-secondary" aria-label="Go back">Back</button>
        {isEditable && invoice.projectStatus === 'INV' && (
          <button onClick={handleApprove} className="btn-success" disabled={!isCompleted || submitting} aria-label="Approve invoice">
            {submitting ? 'Approving...' : 'Approve'}
          </button>
        )}
      </div>
    </div>
  );
};

export default DetailedInvoiceDetails;
